import React from 'react';
import { Box } from '@interstate/components/Box';
import { Action } from '@interstate/components/Action';
import { IconButton } from '@interstate/components/IconButton';
import { TrashIcon } from '@interstate/components/Icons/TrashIcon';
import { Typography } from '@interstate/components/Typography';
import { formatFileSize } from './utils';
import logoPlaceholder from './img/upload_logo.svg';
import type { FileCardProps } from './types';
import styles from './FileCard.module.css';

export const FileCard: React.FC<FileCardProps> = ({
    logoUrl,
    isLoading,
    uploadedFileName,
    uploadedFileSize,
    imageWidth,
    imageHeight,
    isDragActive,
    onBrowseClick,
    onDragOver,
    onDragEnter,
    onDragLeave,
    onDrop,
    onDelete
}) => {
    if (logoUrl) {
        return (
            <Box data-testid="file-card" className={styles.card}>
                <img src={logoUrl} alt="Dealership logo preview" data-testid="logo-thumbnail" className={styles.thumbnail} />
                <Box data-testid="file-info" className={styles.fileInfo}>
                    <Typography variant="h6" tag="p" data-testid="file-name" className={styles.fileName}>
                        {uploadedFileName || 'Uploaded logo'}
                    </Typography>
                    {uploadedFileSize > 0 && (
                        <Typography variant="body-xs" tag="p" color="base.color.gray.600" data-testid="file-size">
                            {formatFileSize(uploadedFileSize)}
                            {imageWidth > 0 && imageHeight > 0 && (
                                <span data-testid="image-dimensions">
                                    {' · '}
                                    {imageWidth} × {imageHeight} px
                                </span>
                            )}
                        </Typography>
                    )}
                </Box>
                <IconButton
                    data-testid="delete-logo-button"
                    buttonStyle="secondary"
                    size="sm"
                    onClick={onDelete}
                    disabled={isLoading}
                    aria-label="Remove logo"
                >
                    <TrashIcon />
                </IconButton>
            </Box>
        );
    }

    return (
        <Box
            data-testid="file-card"
            role="region"
            aria-label="Logo upload drop zone"
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={`${styles.card} ${styles.cardDropTarget}${isDragActive ? ` ${styles.cardDragActive}` : ''}`}
            data-drag-active={isDragActive ? 'true' : undefined}
        >
            <img
                src={logoPlaceholder}
                alt="Dealership logo upload placeholder"
                data-testid="logo-thumbnail-placeholder"
                className={styles.thumbnail}
            />
            <Typography variant="body-sm" tag="p" data-testid="drag-drop-text">
                <span>Drag and drop a file here, or </span>
                <Action data-testid="browse-link" onClick={onBrowseClick} disabled={isLoading} size="sm">
                    browse
                </Action>
            </Typography>
        </Box>
    );
};
